/**
 * IFRAP-AI.fr - KPI Dashboard Page (Admin)
 * 15 economic indicators with criticality tracking
 */

import React, { useState, useEffect } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_NAMES = {
  finances_publiques: 'Finances Publiques',
  macroeconomie: 'Macroéconomie',
  emploi: 'Emploi',
  commerce: 'Commerce',
  energie: 'Énergie',
};

const CRITICALITY_COLORS = {
  normal: '#33FF00',     // Vert
  elevated: '#FFB000',   // Ambre
  high: '#FF6600',       // Orange
  critical: '#FF3333',   // Rouge
};

const KPIsPage = () => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedKpi, setSelectedKpi] = useState(null);
  const [kpiDetail, setKpiDetail] = useState(null);

  useEffect(() => {
    fetchDashboard();
    const interval = setInterval(fetchDashboard, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await fetch(`${API}/kpis/dashboard`);
      if (response.ok) {
        const data = await response.json();
        setDashboard(data);
      }
    } catch (err) {
      console.error('Failed to fetch KPI dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchKpiDetail = async (code) => {
    try {
      const response = await fetch(`${API}/kpis/${code}?include_history=true`);
      if (response.ok) {
        const data = await response.json();
        setKpiDetail(data);
      }
    } catch (err) {
      console.error('Failed to fetch KPI detail:', err);
    }
  };

  const handleKpiClick = (code) => {
    setSelectedKpi(code);
    fetchKpiDetail(code);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500 font-mono">Chargement des indicateurs...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="kpis-page">
      {/* Header with Max Criticality */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-mono text-[#FFB000]">Dashboard KPI France</h1>
          <p className="text-gray-500 text-sm">15 indicateurs économiques clés</p>
        </div>
        
        {/* Criticality Block */}
        <div 
          className="px-6 py-4 rounded-lg border-2"
          style={{
            borderColor: CRITICALITY_COLORS[dashboard?.max_criticality] || '#333',
            backgroundColor: `${CRITICALITY_COLORS[dashboard?.max_criticality]}10`,
          }}
          data-testid="criticality-block"
        >
          <div className="text-xs text-gray-400 uppercase mb-1">Criticité Max</div>
          <div 
            className="text-2xl font-mono font-bold uppercase"
            style={{ color: CRITICALITY_COLORS[dashboard?.max_criticality] }}
          >
            {dashboard?.max_criticality || 'N/A'}
          </div>
          {dashboard?.critical_kpis?.length > 0 && (
            <div className="text-xs text-gray-500 mt-1">
              {dashboard.critical_kpis.length} indicateur(s) en alerte
            </div>
          )}
        </div>
      </div>

      {/* Mock Data Notice */}
      {dashboard?.data_notice && (
        <div className="p-3 bg-[#FFB000]/10 border border-[#FFB000]/30 rounded text-center">
          <span className="text-[#FFB000] text-sm font-mono">{dashboard.data_notice}</span>
        </div>
      )}

      {/* KPI Grid by Category */}
      <div className="space-y-8">
        {dashboard?.categories && Object.entries(dashboard.categories).map(([categoryKey, category]) => (
          <div key={categoryKey}>
            <h2 className="text-lg font-mono text-gray-300 mb-4">{CATEGORY_NAMES[categoryKey] || category.name}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {category.kpis.map((kpi) => (
                <div
                  key={kpi.code}
                  onClick={() => handleKpiClick(kpi.code)}
                  className={`bg-[#0a0a0a] border rounded-lg p-4 cursor-pointer transition-all hover:border-[#FFB000] ${
                    selectedKpi === kpi.code ? 'border-[#FFB000]' : 'border-[#1a1a1a]'
                  }`}
                  data-testid={`kpi-card-${kpi.code}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <span className="text-gray-400 text-sm">{kpi.name}</span>
                    <div className="flex items-center space-x-2">
                      {kpi.is_mock && (
                        <span className="text-xs bg-gray-700 text-gray-400 px-1.5 py-0.5 rounded">MOCK</span>
                      )}
                      <span
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: CRITICALITY_COLORS[kpi.criticality] }}
                      />
                    </div>
                  </div>
                  
                  <div className="flex items-baseline space-x-2">
                    <span className="text-2xl font-mono text-white">{kpi.value_formatted}</span>
                  </div>
                  
                  <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
                    <span>{kpi.period}</span>
                    <span>{kpi.source}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* KPI Detail Modal */}
      {selectedKpi && kpiDetail && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setSelectedKpi(null)}>
          <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-6 max-w-2xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl font-mono text-[#FFB000]">{kpiDetail.definition.name}</h3>
                <p className="text-gray-500 text-sm mt-1">{kpiDetail.definition.description}</p>
              </div>
              <button 
                onClick={() => setSelectedKpi(null)}
                className="text-gray-500 hover:text-white"
              >
                ✕
              </button>
            </div>

            {/* Current Value */}
            {kpiDetail.current_value && (
              <div className="bg-[#1a1a1a] rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-3xl font-mono text-white">{kpiDetail.current_value.value_formatted}</div>
                    <div className="text-sm text-gray-500 mt-1">{kpiDetail.current_value.period}</div>
                  </div>
                  <div 
                    className="px-4 py-2 rounded text-sm font-mono uppercase"
                    style={{
                      backgroundColor: `${CRITICALITY_COLORS[kpiDetail.current_value.criticality]}20`,
                      color: CRITICALITY_COLORS[kpiDetail.current_value.criticality],
                    }}
                  >
                    {kpiDetail.current_value.criticality}
                  </div>
                </div>
              </div>
            )}

            {/* History */}
            {kpiDetail.history?.length > 0 && (
              <div>
                <h4 className="text-sm text-gray-400 mb-2">Historique</h4>
                <div className="space-y-2">
                  {kpiDetail.history.slice(0, 5).map((val, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">{val.period}</span>
                      <div className="flex items-center space-x-2">
                        <span className="text-white font-mono">{val.value}</span>
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: CRITICALITY_COLORS[val.criticality] }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Source */}
            <div className="mt-4 pt-4 border-t border-[#333] text-sm text-gray-500">
              Source: {kpiDetail.definition.source_primary}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KPIsPage;
